import {
  Box,
  Stack,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Alert,
  Paper,
  Container,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Menu,
  MenuItem,
  Toolbar,
  InputAdornment,
  OutlinedInput,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  Tooltip,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState, useEffect, useMemo } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { useParams, useHistory } from 'react-router-dom';

import {
  useGetGroupPosts,
  useCreateGroupPost,
  useGetReplies,
  PostResponse,
  useTogglePostLike,
  useCreateReply,
  useToggleReplyLike,
  ReplyResponse,
} from '@/api/endpoints/community/posts';
import { useCloseGroupNegotiation } from '@/api/endpoints/community/useCloseGroupNegotiation';
import { useGenerateNegotiationResearch } from '@/api/endpoints/community/useGenerateNegotiationResearch';
import {
  useGetGroupNegotiation,
  GroupNegotiationMember,
} from '@/api/endpoints/community/useGetGroupNegotiation';
import { useJoinGroupNegotiation } from '@/api/endpoints/community/useJoinGroupNegotiation';
import { useLeaveGroupNegotiation } from '@/api/endpoints/community/useLeaveGroupNegotiation';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { InfoCircleIcon } from '@/assets/icons/InfoCircleIcon';
import { KeyboardReturnIcon } from '@/assets/icons/KeyboardReturnIcon';
import { MagicWand01Icon } from '@/assets/icons/MagicWand01Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { MessageChatCircleIcon } from '@/assets/icons/MessageChatCircleIcon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Share01Icon } from '@/assets/icons/Share01Icon';
import { User01Icon } from '@/assets/icons/User01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import ShareGroupModal from '@/components/community/ShareGroupModal';
import { GroupPostsTab } from '@/components/posts';
import { PostForm, PostFormData } from '@/components/posts/PostForm';
import NegotiationAITab from '@/components/property-manager-portal/community/NegotiationAITab';

// Member Card Component for Mobile Layout
interface MemberCardProps {
  member: GroupNegotiationMember;
  isCreator: boolean;
  getAvatarProps: (name: string) => { children: string };
}

const MemberCard: React.FC<MemberCardProps> = ({ member, isCreator, getAvatarProps }) => (
  <Card
    sx={{
      mb: 1.5,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: '1px solid',
      borderColor: 'rgba(0, 0, 0, 0.12)',
      bgcolor: 'background.paper',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderRadius: 1.5,
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    }}
  >
    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
      {/* Header Section */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              {...getAvatarProps(member.user.full_name)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 40,
                height: 40,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            />
            <Stack spacing={0.25} sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    lineHeight: 1.2,
                  }}
                >
                  {member.user.full_name}
                </Typography>
                {isCreator && (
                  <Chip
                    label="Creator"
                    size="small"
                    sx={{
                      fontSize: '0.6rem',
                      height: 16,
                      width: 'fit-content',
                      fontWeight: 500,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& .MuiChip-label': {
                        padding: '0 6px',
                      },
                    }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Member Details Section */}
      <Box sx={{ p: 1.5, pt: 0.75 }}>
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                minWidth: 'fit-content',
              }}
            >
              Joined:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 500,
                flex: 1,
              }}
            >
              {formatDistanceToNow(new Date(member.joined_at), { addSuffix: true })}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </CardContent>
  </Card>
);

// Component for individual post discussion
const PostDiscussion: React.FC<{
  post: PostResponse;
  index: number;
  totalPosts: number;
  communityId: number;
  replyInputs: { [postId: number]: string };
  showReplyForms: { [postId: number]: boolean };
  expandedReplies: Record<string, boolean>;
  onTogglePostLike: (postId: number) => void;
  onShowReplyForm: (postId: number, show: boolean) => void;
  onReplyInputChange: (postId: number, value: string) => void;
  onSubmitReply: (postId: number) => void;
  onToggleExpandedReplies: (postId: number) => void;
  onToggleReplyLike: (replyId: number, postId: number) => void;
  getAvatarProps: (name: string) => { children: string };
  togglePostLikeMutation: { isPending: boolean };
  createReplyMutation: ReturnType<typeof import('@/api/endpoints/community/posts').useCreateReply>;
}> = ({
  post,
  index,
  totalPosts,
  communityId,
  replyInputs,
  showReplyForms,
  expandedReplies,
  onTogglePostLike,
  onShowReplyForm,
  onReplyInputChange,
  onSubmitReply,
  onToggleExpandedReplies,
  onToggleReplyLike,
  getAvatarProps,
  togglePostLikeMutation,
  createReplyMutation,
}) => {
  // State for reply-to-reply functionality
  const [replyToReplyForms, setReplyToReplyForms] = useState<{ [replyId: number]: boolean }>({});
  const [replyToReplyInputs, setReplyToReplyInputs] = useState<{ [replyId: number]: string }>({});

  const handleShowReplyToReplyForm = (replyId: number, show: boolean) => {
    if (show) {
      // Close all other reply forms and open only this one
      setReplyToReplyForms({ [replyId]: true });
      setReplyToReplyInputs({ [replyId]: '' });
    } else {
      // Close this specific reply form
      setReplyToReplyForms(prev => ({ ...prev, [replyId]: false }));
      setReplyToReplyInputs(prev => ({ ...prev, [replyId]: '' }));
    }
  };

  const handleReplyToReplyInputChange = (replyId: number, value: string) => {
    setReplyToReplyInputs(prev => ({ ...prev, [replyId]: value }));
  };

  const handleSubmitReplyToReply = async (replyId: number) => {
    const content = replyToReplyInputs[replyId]?.trim();
    if (!content) return;

    try {
      await createReplyMutation.mutateAsync({
        postId: post.id,
        communityId,
        content,
        parentId: replyId,
      });
      handleShowReplyToReplyForm(replyId, false);
    } catch (error) {
      // Error handling is managed by the mutation
      // eslint-disable-next-line no-console
      console.error('Failed to create reply:', error);
    }
  };

  // Override the main reply form handler to close reply-to-reply forms
  const handleShowMainReplyForm = (postId: number, show: boolean) => {
    if (show) {
      // Close all reply-to-reply forms when opening main reply form
      setReplyToReplyForms({});
      setReplyToReplyInputs({});
    }
    onShowReplyForm(postId, show);
  };
  const { data: replies = [] } = useGetReplies({ postId: post.id, page: 1, limit: 3 }, true);

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to render all replies in a flat structure
  const renderAllReplies = (repliesData: ReplyResponse[], postId: number) => {
    const formatTimeAgoLocal = (dateString: string) => {
      const now = new Date();
      const postDate = new Date(dateString);
      const diffInHours = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    };

    // Flatten all replies into a single array with their levels and parent info
    const flattenReplies = (
      repliesToFlatten: ReplyResponse[],
      parentReply?: ReplyResponse,
      level = 0
    ): Array<{ reply: ReplyResponse; level: number; parentReply?: ReplyResponse }> => {
      const result: Array<{ reply: ReplyResponse; level: number; parentReply?: ReplyResponse }> =
        [];

      repliesToFlatten.forEach(reply => {
        result.push({ reply, level, parentReply });
        if (reply.nested_replies && reply.nested_replies.length > 0) {
          result.push(...flattenReplies(reply.nested_replies, reply, level + 1));
        }
      });

      return result;
    };

    const flattenedReplies = flattenReplies(repliesData);

    return flattenedReplies.map(({ reply, level, parentReply }) => (
      <Box key={reply.id} sx={{ mb: level > 0 ? 1 : 1.5 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          sx={{
            ml: level >= 1 ? 3 : 0,
            pl: level > 1 ? 2 : 0,
          }}
        >
          <Avatar
            {...getAvatarProps(reply.author.user.full_name || reply.author.user.email)}
            sx={{
              width: level === 0 ? 24 : 20,
              height: level === 0 ? 24 : 20,
              fontSize: level === 0 ? '0.75rem' : '0.7rem',
              fontWeight: 600,
              bgcolor: alpha('#9D1500', 0.2),
              color: 'primary.main',
              mt: 0.25,
            }}
          />
          <Box sx={{ flex: 1 }}>
            {/* Reply Header with Reply Indicator */}
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'secondary.900',
                  fontSize: level > 0 ? '0.7rem' : '0.75rem',
                  display: 'inline',
                }}
              >
                {reply.author.user.full_name || reply.author.user.email}
              </Typography>
              {level > 0 && parentReply && (
                <>
                  <KeyboardReturnIcon
                    sx={{
                      width: 12,
                      height: 12,
                      color: 'secondary.500',
                      transform: 'scaleX(-1)',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.900',
                      fontSize: '0.7rem',
                      display: 'inline',
                    }}
                  >
                    {parentReply.author.user.full_name || parentReply.author.user.email}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Reply Content */}
            <Typography
              variant="body2"
              sx={{
                color: 'secondary.800',
                fontSize: level > 0 ? '0.7rem' : '0.75rem',
                display: 'block',
                mb: 0.5,
              }}
            >
              {reply.content}
            </Typography>

            {/* Reply Actions */}
            <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'secondary.600',
                  fontSize: '0.7rem',
                }}
              >
                {formatTimeAgoLocal(reply.created_at)}
              </Typography>
              <Button
                size="small"
                onClick={() => onToggleReplyLike(reply.id, postId)}
                disabled={false}
                sx={{
                  color: reply.is_liked ? 'primary.main' : 'secondary.600',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                  },
                }}
              >
                {reply._count.likes > 0 ? `${reply._count.likes} ` : ''}Like
              </Button>
              <Button
                size="small"
                onClick={() => handleShowReplyToReplyForm(reply.id, !replyToReplyForms[reply.id])}
                sx={{
                  color: 'secondary.600',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                  },
                }}
              >
                Reply
              </Button>
            </Stack>

            {/* Reply-to-Reply Form */}
            {replyToReplyForms[reply.id] && (
              <Box sx={{ mt: 1, mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    placeholder="Write a reply..."
                    value={replyToReplyInputs[reply.id] || ''}
                    onChange={e => handleReplyToReplyInputChange(reply.id, e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        minHeight: 'auto',
                        height: 'auto',
                        '& .MuiInputBase-input': {
                          padding: '0px 8px',
                          lineHeight: 1.2,
                          height: '34px',
                        },
                      },
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitReplyToReply(reply.id);
                      }
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => handleSubmitReplyToReply(reply.id)}
                    disabled={!replyToReplyInputs[reply.id]?.trim()}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                      '&:disabled': {
                        bgcolor: 'secondary.300',
                        color: 'secondary.500',
                      },
                    }}
                  >
                    Post
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    ));
  };

  return (
    <Box sx={{ mb: index < totalPosts - 1 ? 3 : 0 }}>
      {/* Post Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <Avatar
          {...getAvatarProps(post.author.user.full_name || post.author.user.email)}
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            fontWeight: 600,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'secondary.900',
              fontSize: '0.875rem',
            }}
          >
            {post.author.user.full_name || post.author.user.email}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'secondary.600',
              fontSize: '0.7rem',
            }}
          >
            {formatTimeAgo(post.created_at)}
          </Typography>
        </Box>
      </Stack>

      {/* Post Content */}
      <Typography
        variant="body2"
        sx={{
          color: 'secondary.800',
          lineHeight: 1.5,
          mb: 1.5,
          fontSize: '0.875rem',
        }}
      >
        {post.title && (
          <Box component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            {post.title}
          </Box>
        )}
        {post.content}
      </Typography>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
        <Button
          size="small"
          onClick={() => onTogglePostLike(post.id)}
          disabled={togglePostLikeMutation.isPending}
          sx={{
            color: post.is_liked ? 'primary.main' : 'secondary.600',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              bgcolor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {post._count.likes > 0 ? `${post._count.likes} ` : ''}Like
        </Button>
        <Button
          size="small"
          onClick={() => handleShowMainReplyForm(post.id, !showReplyForms[post.id])}
          sx={{
            color: 'secondary.600',
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
            '&:hover': {
              bgcolor: 'transparent',
              color: 'primary.main',
            },
          }}
        >
          {post._count.replies > 0 ? `${post._count.replies} ` : ''}Reply
        </Button>
      </Stack>

      {/* Reply Form */}
      {showReplyForms[post.id] && (
        <Box sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              placeholder="Write a reply..."
              value={replyInputs[post.id] || ''}
              onChange={e => onReplyInputChange(post.id, e.target.value)}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.75rem',
                  borderRadius: 2,
                  minHeight: 'auto',
                  height: 'auto',
                  '& .MuiInputBase-input': {
                    padding: '0px 8px',
                    lineHeight: 1.2,
                    height: '34px',
                  },
                },
              }}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSubmitReply(post.id);
                }
              }}
            />
            <Button
              size="small"
              onClick={() => onSubmitReply(post.id)}
              disabled={!replyInputs[post.id]?.trim() || createReplyMutation.isPending}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'secondary.300',
                  color: 'secondary.500',
                },
              }}
            >
              {createReplyMutation.isPending ? 'Posting...' : 'Post'}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Comments Section */}
      {replies.length > 0 && (
        <Box>
          {renderAllReplies(expandedReplies[post.id] ? replies : replies.slice(0, 2), post.id)}
          {replies.length > 2 && (
            <Button
              size="small"
              onClick={() => onToggleExpandedReplies(post.id)}
              sx={{
                color: 'secondary.600',
                textTransform: 'none',
                fontSize: '0.7rem',
                fontWeight: 500,
                p: 0,
                minWidth: 'auto',
                mt: 0.5,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                },
              }}
            >
              {expandedReplies[post.id]
                ? 'Show less'
                : `View ${replies.length - 2} more replies...`}
            </Button>
          )}
        </Box>
      )}

      {/* Divider between posts */}
      {index < totalPosts - 1 && (
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            mt: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 },
            mx: { xs: -2, sm: 0 },
          }}
        />
      )}
    </Box>
  );
};

// Define markdown components outside render to avoid ESLint warnings
const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography
      variant="body1"
      sx={{
        mb: { xs: 0.75, sm: 1 },
        fontSize: { xs: '0.875rem', sm: '1rem' },
        lineHeight: { xs: 1.5, sm: 1.6 },
      }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography
      variant="h4"
      sx={{
        mb: { xs: 1.5, sm: 2 },
        fontWeight: 'bold',
        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="h5"
      sx={{
        mb: { xs: 1, sm: 1.5 },
        fontWeight: 'bold',
        fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="h6"
      sx={{
        mb: { xs: 0.75, sm: 1 },
        fontWeight: 'bold',
        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Typography
      component="ul"
      sx={{
        mb: { xs: 0.75, sm: 1 },
        pl: { xs: 1.5, sm: 2 },
        fontSize: { xs: '0.875rem', sm: '1rem' },
      }}
    >
      {children}
    </Typography>
  ),
  ol: ({ children }) => (
    <Typography
      component="ol"
      sx={{
        mb: { xs: 0.75, sm: 1 },
        pl: { xs: 1.5, sm: 2 },
        fontSize: { xs: '0.875rem', sm: '1rem' },
      }}
    >
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography
      component="li"
      variant="body1"
      sx={{
        mb: { xs: 0.25, sm: 0.5 },
        fontSize: { xs: '0.875rem', sm: '1rem' },
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography
      component="strong"
      sx={{
        fontWeight: 'bold',
        fontSize: { xs: '0.875rem', sm: '1rem' },
      }}
    >
      {children}
    </Typography>
  ),
};

const GroupNegotiationDetailPage: React.FC = () => {
  const { communityId, negotiationId } = useParams<{
    communityId: string;
    negotiationId: string;
  }>();
  const history = useHistory();
  const { user } = useAuth();
  const { propertyLocations, isFetching: isLoadingUserProperty } = usePropertyManager();
  const userHasLocation = propertyLocations && propertyLocations.length > 0;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isProcessing, setIsProcessing] = useState(false);
  const [closeConfirmDialogOpen, setCloseConfirmDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [aiResearchResponse, setAiResearchResponse] = useState<string>('');
  const [hasAutoLoadedResearch, setHasAutoLoadedResearch] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [aiResearchError, setAiResearchError] = useState(false);
  const [showLocationAlert, setShowLocationAlert] = useState(false);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<null | HTMLElement>(null);
  const [showCreatePostForm, setShowCreatePostForm] = useState(false);
  const [filterName, setFilterName] = useState('');

  // Posts state variables
  const [replyInputs, setReplyInputs] = useState<{ [postId: number]: string }>({});
  const [showReplyForms, setShowReplyForms] = useState<{ [postId: number]: boolean }>({});
  const [expandedReplies, setExpandedReplies] = useState<Record<string, boolean>>({});

  const currentUserId = user?.id;

  const {
    data: groupNegotiation,
    error,
    isLoading,
  } = useGetGroupNegotiation({
    communityId: parseInt(communityId, 10),
    negotiationId: parseInt(negotiationId, 10),
  });

  const joinMutation = useJoinGroupNegotiation();
  const leaveMutation = useLeaveGroupNegotiation();
  const closeMutation = useCloseGroupNegotiation();
  const { mutate: generateResearch, isPending: isResearchLoading } =
    useGenerateNegotiationResearch();

  // Posts API hooks
  const { data: posts = [] } = useGetGroupPosts({
    groupId: parseInt(negotiationId, 10),
    page: 1,
    limit: 3,
  });

  const createPostMutation = useCreateGroupPost();
  const togglePostLikeMutation = useTogglePostLike();
  const createReplyMutation = useCreateReply();
  const toggleReplyLikeMutation = useToggleReplyLike();

  // Auto-trigger AI Research request when Details tab is active and user is a member
  useEffect(() => {
    const userIsMember = groupNegotiation?.members.some(member => member.user.id === currentUserId);

    if (
      !hasAutoLoadedResearch &&
      negotiationId &&
      communityId &&
      activeTab === 0 &&
      groupNegotiation &&
      userIsMember
    ) {
      setHasAutoLoadedResearch(true);
      generateResearch(
        {
          community_id: parseInt(communityId, 10),
          negotiation_id: parseInt(negotiationId, 10),
        },
        {
          onSuccess: (data: { response: string }) => {
            setAiResearchResponse(data.response);
            setAiResearchError(false);
          },
          onError: () => {
            setAiResearchError(true);
          },
        }
      );
    }
  }, [
    hasAutoLoadedResearch,
    negotiationId,
    communityId,
    activeTab,
    groupNegotiation,
    generateResearch,
    currentUserId,
  ]);

  const handleBack = () => {
    history.push(`${PMPRoutes.communityView.replace(':id', communityId)}?tab=negotiations`);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleJoin = async () => {
    if (!userHasLocation) {
      setShowLocationAlert(true);
      return;
    }

    if (isProcessing) return;
    setIsProcessing(true);

    try {
      await joinMutation.mutateAsync({
        communityId: parseInt(communityId, 10),
        negotiationId: parseInt(negotiationId, 10),
      });
    } catch (joinError) {
      // Handle error silently or show user-friendly message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLeave = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      await leaveMutation.mutateAsync({
        communityId: parseInt(communityId, 10),
        negotiationId: parseInt(negotiationId, 10),
      });
    } catch (leaveError) {
      // Handle error silently or show user-friendly message
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCloseConfirmDialogOpen(true);
  };

  const confirmCloseGroup = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      await closeMutation.mutateAsync({
        communityId: parseInt(communityId, 10),
        negotiationId: parseInt(negotiationId, 10),
      });
      // Navigate back after closing
      handleBack();
    } catch (closeError) {
      // Handle error silently or show user-friendly message
    } finally {
      setIsProcessing(false);
      setCloseConfirmDialogOpen(false);
    }
  };

  const handleGroupMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setGroupMenuAnchor(event.currentTarget);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchor(null);
  };

  const handleCreatePostClick = () => {
    setShowCreatePostForm(true);
  };

  const handleCreatePost = async (data: PostFormData) => {
    await createPostMutation.mutateAsync({
      groupId: parseInt(negotiationId, 10),
      ...data,
    });
    setShowCreatePostForm(false);
  };

  const handleCancelCreatePost = () => {
    setShowCreatePostForm(false);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  };

  // Posts handler functions
  const handleTogglePostLike = async (postId: number) => {
    await togglePostLikeMutation.mutateAsync({
      postId,
      groupId: parseInt(negotiationId, 10),
    });
  };

  const handleCreateReply = async (postId: number, content: string) => {
    await createReplyMutation.mutateAsync({
      postId,
      groupId: parseInt(negotiationId, 10),
      content,
    });
  };

  const handleToggleReplyLike = async (replyId: number, postId: number) => {
    await toggleReplyLikeMutation.mutateAsync({
      replyId,
      postId,
      groupId: parseInt(negotiationId, 10),
    });
  };

  const handleReplyInputChange = (postId: number, value: string) => {
    setReplyInputs(prev => ({ ...prev, [postId]: value }));
  };

  const handleShowReplyForm = (postId: number, show: boolean) => {
    setShowReplyForms(prev => ({ ...prev, [postId]: show }));
    if (!show) {
      setReplyInputs(prev => ({ ...prev, [postId]: '' }));
    }
  };

  const handleSubmitReply = async (postId: number) => {
    const content = replyInputs[postId]?.trim();
    if (!content) return;

    await handleCreateReply(postId, content);
    handleShowReplyForm(postId, false);
  };

  const handleToggleExpandedReplies = (postId: number) => {
    setExpandedReplies(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const filteredMembers = useMemo(() => {
    if (!groupNegotiation?.members) return [];

    if (filterName) {
      return groupNegotiation.members.filter(member =>
        member.user.full_name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    return groupNegotiation.members;
  }, [groupNegotiation?.members, filterName]);

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    return {
      children: initials,
    };
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const renderEmptyState = () => (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 1.5,
        }}
      >
        <Typography variant="h5" color="text.secondary">
          U
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.875rem' }}>
        {filterName ? 'No members found' : 'No members yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.7rem' }}>
        {filterName
          ? 'Try adjusting your search terms.'
          : 'Members will appear here when they join the group.'}
      </Typography>
    </Box>
  );

  const renderMobileMembersList = () => {
    if (!groupNegotiation) return null;

    return (
      <Stack spacing={0}>
        {filteredMembers.map(member => (
          <MemberCard
            key={member.id}
            member={member}
            isCreator={member.user.id === groupNegotiation.initiator.user_id}
            getAvatarProps={getAvatarProps}
          />
        ))}
      </Stack>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
    case 'ACTIVE':
      return '#4caf50';
    case 'CLOSED':
      return '#f44336';
    case 'CANCELLED':
      return '#ff9800';
    default:
      return '#757575';
    }
  };

  const isUserMember = groupNegotiation?.members.some(member => member.user.id === currentUserId);

  const isUserInitiator = groupNegotiation?.initiator.user_id === currentUserId;

  const isGroupExpired = groupNegotiation?.end_date
    ? new Date(groupNegotiation.end_date) < new Date()
    : false;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Alert severity="error">Error loading group: {error.message}</Alert>
        <Button startIcon={<ArrowLeftIcon />} onClick={handleBack}>
          Back to Community
        </Button>
      </Stack>
    );
  }

  if (!groupNegotiation) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Alert severity="warning">Group not found</Alert>
        <Button startIcon={<ArrowLeftIcon />} onClick={handleBack}>
          Back to Community
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 2 }}>
      <Container maxWidth="xl" sx={{ pt: 1 }}>
        <Stack direction="column" spacing={2.5}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              background: `linear-gradient(135deg, ${alpha('#1976d2', 0.02)} 0%, transparent 100%)`,
              position: 'relative',
            }}
          >
            {/* Menu Button - Right Side */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                zIndex: 1,
              }}
            >
              <IconButton
                onClick={handleGroupMenuClick}
                sx={{
                  color: 'secondary.400',
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: alpha('#9D1500', 0.08),
                  },
                }}
              >
                <DotsVerticalIcon />
              </IconButton>
            </Box>

            <Stack direction="column" justifyContent="space-between">
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={2}
              >
                <IconButton
                  onClick={handleBack}
                  sx={{
                    color: 'secondary.400',
                    display: { xs: 'none', sm: 'flex' },
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: alpha('#9D1500', 0.08),
                    },
                  }}
                >
                  <ArrowLeftIcon sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }} />
                </IconButton>
                {groupNegotiation.status === 'ACTIVE' && !isUserMember && !isGroupExpired && (
                  <Tooltip
                    title={
                      !userHasLocation && !isLoadingUserProperty ? (
                        <Stack spacing={1}>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            You need to add your location to join Save Together groups
                          </Typography>
                          <Button
                            variant="text"
                            size="small"
                            sx={{
                              color: 'white',
                              p: 0,
                              minWidth: 'auto',
                              '&:hover': {
                                textDecoration: 'underline',
                                bgcolor: 'transparent',
                                color: 'white',
                              },
                            }}
                            onClick={() => {
                              const currentPath = `/property-manager/communities/${communityId}/groups/${negotiationId}`;
                              history.push(
                                `/property-manager/property-locations/create?returnUrl=${encodeURIComponent(
                                  currentPath
                                )}`
                              );
                            }}
                          >
                            Add your location now →
                          </Button>
                        </Stack>
                      ) : (
                        ''
                      )
                    }
                    placement="bottom"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'grey.800',
                          '& .MuiTooltip-arrow': {
                            color: 'grey.800',
                          },
                        },
                      },
                    }}
                  >
                    <span>
                      <Button
                        startIcon={<Users02Icon />}
                        variant="contained"
                        disabled={isProcessing || !userHasLocation || isLoadingUserProperty}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          fontWeight: 500,
                          px: 3,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500',
                            cursor:
                              !userHasLocation && !isLoadingUserProperty
                                ? 'pointer'
                                : 'not-allowed',
                          },
                        }}
                        onClick={
                          !userHasLocation && !isLoadingUserProperty
                            ? () => setShowLocationAlert(true)
                            : handleJoin
                        }
                      >
                        {isProcessing ? 'Joining...' : 'Join Group'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 2,
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.75rem', sm: '1.75rem', md: '2.25rem' },
                      lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
                      mb: { xs: 1, sm: 1.5 },
                    }}
                  >
                    {groupNegotiation.title}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mt: { xs: 1, sm: 2 },
                      mb: { xs: 1, sm: 2 },
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                    }}
                  >
                    <Chip
                      label={groupNegotiation.status}
                      size="small"
                      sx={{
                        bgcolor: alpha(getStatusColor(groupNegotiation.status), 0.1),
                        color: getStatusColor(groupNegotiation.status),
                        fontWeight: 500,
                        fontSize: { xs: '0.7rem', sm: '0.65rem' },
                        height: { xs: 20, sm: 20 },
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 0.75 },
                        },
                      }}
                    />
                    <Chip
                      label={`${groupNegotiation.members.length} member${
                        groupNegotiation.members.length === 1 ? '' : 's'
                      }`}
                      size="small"
                      sx={{
                        bgcolor: alpha('#2196f3', 0.1),
                        color: '#1976d2',
                        fontWeight: 500,
                        fontSize: { xs: '0.7rem', sm: '0.65rem' },
                        height: { xs: 20, sm: 20 },
                        '& .MuiChip-label': {
                          px: { xs: 0.75, sm: 0.75 },
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Tab Navigation */}
          <Tabs
            value={activeTab}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: { xs: 0, sm: 'auto' },
                px: { xs: 1, sm: 2 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          >
            <Tab
              icon={<InfoCircleIcon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
              iconPosition="start"
              label="Details"
            />
            {isUserMember && (
              <Tab
                icon={<Users02Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
                iconPosition="start"
                label="Members"
              />
            )}
            <Tab
              icon={
                <MessageChatCircleIcon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />
              }
              iconPosition="start"
              label="Posts"
            />
            {isUserMember && groupNegotiation.status === 'ACTIVE' && (
              <Tab
                icon={<MagicWand01Icon width={isMobile ? 14 : 16} height={isMobile ? 14 : 16} />}
                iconPosition="start"
                label="Negotiation AI"
              />
            )}
          </Tabs>

          {/* Tab Content */}
          {/* Details Tab */}
          {activeTab === 0 && (
            <Stack spacing={{ xs: 2, sm: 3 }}>
              {/* Group Description - Full width on mobile, full width on desktop */}
              {groupNegotiation.description && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.900',
                      mb: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                    }}
                  >
                    Group Description
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'secondary.700',
                      lineHeight: { xs: 1.5, sm: 1.6 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {groupNegotiation.description}
                  </Typography>
                </Paper>
              )}

              {/* Pricing Tiers - PRIMARY SECTION - Show immediately after description */}
              {(() => {
                const metadata = groupNegotiation.metadata as
                  | {
                      originalCost?: number;
                      savingsAmount?: number;
                      estimated_savings?: number;
                      pricingTiers?: Array<{
                        minHomes: number;
                        maxHomes?: number;
                        pricePerHome: number;
                      }>;
                    }
                  | undefined;

                if (metadata?.pricingTiers && metadata.pricingTiers.length > 0) {
                  const currentMemberCount = groupNegotiation.members.length;

                  // Find current tier
                  const currentTier = metadata.pricingTiers.find(
                    tier =>
                      currentMemberCount >= tier.minHomes &&
                      (!tier.maxHomes || currentMemberCount <= tier.maxHomes)
                  );

                  // Find next tier
                  const nextTier = metadata.pricingTiers.find(
                    tier => currentMemberCount < tier.minHomes
                  );

                  // Calculate savings
                  const highestPrice = metadata.pricingTiers[0]?.pricePerHome || 0;
                  const currentPrice = currentTier?.pricePerHome || highestPrice;
                  const currentSavings = highestPrice - currentPrice;
                  const savingsPercentage =
                    highestPrice > 0 ? Math.round((currentSavings / highestPrice) * 100) : 0;

                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        border: '3px solid',
                        borderColor: 'success.300',
                        bgcolor: 'success.50',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(76, 175, 80, 0.15)',
                      }}
                    >
                      {/* Background pattern */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -50,
                          right: -50,
                          width: 200,
                          height: 200,
                          borderRadius: '50%',
                          bgcolor: alpha('#4caf50', 0.1),
                        }}
                      />

                      <Stack spacing={3}>
                        <Box>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              color: 'success.900',
                              mb: 1,
                              fontSize: { xs: '1.5rem', sm: '2rem' },
                            }}
                          >
                            💰 Group Savings Program
                          </Typography>
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                          >
                            The more people who join, the greater the savings for everyone!
                          </Typography>
                        </Box>

                        {/* Current Status - Highlighted */}
                        <Box
                          sx={{
                            p: { xs: 2.5, sm: 3 },
                            borderRadius: 2,
                            bgcolor: 'white',
                            border: '2px solid',
                            borderColor: 'success.300',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'stretch', sm: 'center' }}
                              spacing={{ xs: 2, sm: 0 }}
                            >
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Current Group Size
                                </Typography>
                                <Typography
                                  variant="h3"
                                  fontWeight={700}
                                  color="success.700"
                                  sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}
                                >
                                  {currentMemberCount} {currentMemberCount === 1 ? 'Home' : 'Homes'}
                                </Typography>
                              </Box>
                              <Box textAlign={{ xs: 'left', sm: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  Your Price
                                </Typography>
                                <Typography
                                  variant="h3"
                                  fontWeight={700}
                                  color="success.700"
                                  sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}
                                >
                                  ${currentPrice}/home
                                </Typography>
                                {savingsPercentage > 0 && (
                                  <Chip
                                    label={`Save ${savingsPercentage}%`}
                                    size="medium"
                                    sx={{
                                      bgcolor: 'success.600',
                                      color: 'white',
                                      fontWeight: 700,
                                      fontSize: '0.875rem',
                                      mt: 0.5,
                                      px: 2,
                                    }}
                                  />
                                )}
                              </Box>
                            </Stack>

                            {/* Progress to next tier */}
                            {nextTier && (
                              <Box sx={{ mt: 3 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    Progress to next tier
                                  </Typography>
                                  <Typography variant="body2" fontWeight={600} color="warning.700">
                                    {nextTier.minHomes - currentMemberCount} more{' '}
                                    {nextTier.minHomes - currentMemberCount === 1
                                      ? 'member'
                                      : 'members'}{' '}
                                    needed
                                  </Typography>
                                </Stack>
                                <Box sx={{ position: 'relative' }}>
                                  <Box
                                    sx={{
                                      height: 12,
                                      borderRadius: 6,
                                      bgcolor: 'grey.200',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${(currentMemberCount / nextTier.minHomes) * 100}%`,
                                        bgcolor: 'warning.main',
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Stack>
                        </Box>

                        {/* Pricing Tiers */}
                        <Box>
                          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
                            All Pricing Tiers
                          </Typography>
                          <Stack spacing={1.5}>
                            {metadata.pricingTiers.map((tier, index) => {
                              const isCurrentTier =
                                currentMemberCount >= tier.minHomes &&
                                (!tier.maxHomes || currentMemberCount <= tier.maxHomes);
                              const isAchieved = currentMemberCount >= tier.minHomes;
                              const tierSavings = highestPrice - tier.pricePerHome;
                              const tierSavingsPercent =
                                highestPrice > 0
                                  ? Math.round((tierSavings / highestPrice) * 100)
                                  : 0;

                              return (
                                <Box
                                  key={`pricing-tier-${tier.minHomes}-${tier.maxHomes || 'plus'}`}
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: (() => {
                                      if (isCurrentTier) return 'success.100';
                                      if (isAchieved) return 'grey.50';
                                      return 'white';
                                    })(),
                                    border: '2px solid',
                                    borderColor: (() => {
                                      if (isCurrentTier) return 'success.400';
                                      if (isAchieved) return 'grey.300';
                                      return 'grey.200';
                                    })(),
                                    position: 'relative',
                                    overflow: 'hidden',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                  }}
                                >
                                  {isCurrentTier && (
                                    <Chip
                                      label="CURRENT TIER"
                                      size="small"
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        bgcolor: 'success.600',
                                        color: 'white',
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  )}

                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={2}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography
                                          variant="h6"
                                          fontWeight={600}
                                          color={isCurrentTier ? 'success.800' : 'text.primary'}
                                        >
                                          {tier.maxHomes
                                            ? `${tier.minHomes}-${tier.maxHomes} Homes`
                                            : `${tier.minHomes}+ Homes`}
                                        </Typography>
                                        {isAchieved && !isCurrentTier && (
                                          <CheckCircleIcon
                                            sx={{
                                              width: 20,
                                              height: 20,
                                              color: 'success.main',
                                            }}
                                          />
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5 }}
                                      >
                                        {isAchieved ? 'Unlocked' : 'Locked'}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography
                                        variant="h5"
                                        fontWeight={700}
                                        color={isCurrentTier ? 'success.700' : 'text.primary'}
                                      >
                                        ${tier.pricePerHome}/home
                                      </Typography>
                                      {tierSavingsPercent > 0 && (
                                        <Typography
                                          variant="body2"
                                          color="success.600"
                                          fontWeight={600}
                                        >
                                          Save {tierSavingsPercent}%
                                        </Typography>
                                      )}
                                    </Box>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>

                        {/* Call to Action */}
                        {!isUserMember && groupNegotiation.status === 'ACTIVE' && (
                          <Box
                            sx={{
                              p: 3,
                              borderRadius: 2,
                              bgcolor: alpha('#9D1500', 0.05),
                              border: '2px solid',
                              borderColor: alpha('#9D1500', 0.2),
                              textAlign: 'center',
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight={600}
                              color="primary.main"
                              sx={{ mb: 2 }}
                            >
                              Join now to lock in these savings!
                            </Typography>
                            <Button
                              variant="contained"
                              size="large"
                              onClick={handleJoin}
                              disabled={isProcessing || !userHasLocation}
                              sx={{
                                bgcolor: 'primary.main',
                                px: 4,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'primary.dark' },
                              }}
                            >
                              Join This Group
                            </Button>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  );
                }

                return null;
              })()}

              {/* Desktop Layout - Side by side */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 3 }}>
                {/* Group Overview - 1/4 width */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    flex: 1,
                    height: 'fit-content',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.900',
                      mb: 2,
                      fontSize: '1.25rem',
                    }}
                  >
                    Group Overview
                  </Typography>

                  <Stack spacing={2}>
                    {/* Created Date */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(groupNegotiation.created_at)}
                      </Typography>
                    </Box>

                    {/* End Date */}
                    {groupNegotiation.end_date && (
                      <Box>
                        <Typography
                          variant="body2"
                          color={isGroupExpired ? 'error.main' : 'secondary.600'}
                          sx={{ mb: 0.5 }}
                        >
                          {isGroupExpired ? 'Expired' : 'Ends'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isGroupExpired ? 'error.main' : 'inherit',
                          }}
                        >
                          {formatDate(groupNegotiation.end_date)}
                        </Typography>
                      </Box>
                    )}

                    {/* Initiator */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Initiated by
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {groupNegotiation.initiator.full_name || groupNegotiation.initiator.email}
                      </Typography>
                    </Box>

                    {/* Member Count */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Members
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {groupNegotiation.members.length}{' '}
                        {groupNegotiation.members.length === 1 ? 'member' : 'members'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* AI Research Section - 3/4 width */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    flex: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.900',
                      mb: 2,
                      fontSize: '1.25rem',
                    }}
                  >
                    AI Research
                  </Typography>

                  {isUserMember ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          mb: 2,
                          display: 'block',
                          lineHeight: 1.5,
                        }}
                      >
                        Disclaimer: The AI-generated research provided here is for informational
                        purposes only and should not be considered legal, financial, or professional
                        advice. Please consult with a qualified expert before making any decisions
                        based on this information.
                      </Typography>

                      {/* Show loading state if loading */}
                      {isResearchLoading && !aiResearchResponse && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          minHeight={200}
                        >
                          <CircularProgress />
                        </Box>
                      )}

                      {/* Show AI response if available */}
                      {aiResearchResponse && (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: alpha('#9D1500', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#9D1500', 0.1),
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            color="primary"
                            sx={{
                              fontWeight: 600,
                              fontSize: '1rem',
                              mb: 1.5,
                            }}
                          >
                            AI Research Analysis:
                          </Typography>
                          <ReactMarkdown components={markdownComponents}>
                            {aiResearchResponse}
                          </ReactMarkdown>
                        </Box>
                      )}

                      {/* Show error message if failed */}
                      {aiResearchError && (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: alpha('#f44336', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#f44336', 0.1),
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '1rem' }}
                          >
                            AI research is not available at the moment
                          </Typography>
                        </Box>
                      )}

                      {/* Show placeholder if no response and not loading and no error */}
                      {!isResearchLoading && !aiResearchResponse && !aiResearchError && (
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'secondary.50',
                            border: '2px dashed',
                            borderColor: 'secondary.200',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="secondary.600"
                            sx={{
                              fontStyle: 'italic',
                              fontSize: '1rem',
                            }}
                          >
                            AI research is being generated...
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        bgcolor: alpha('#2196f3', 0.05),
                        border: '1px solid',
                        borderColor: alpha('#2196f3', 0.3),
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        Join the group to access AI-powered research insights
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Cost Information - Show if available in metadata */}
                {(() => {
                  const metadata = groupNegotiation.metadata as
                    | {
                        originalCost?: number;
                        savingsAmount?: number;
                        estimated_savings?: number;
                        pricingTiers?: Array<{
                          minHomes: number;
                          maxHomes?: number;
                          pricePerHome: number;
                        }>;
                      }
                    | undefined;
                  const originalCost = metadata?.originalCost || 0;
                  const savingsAmount = metadata?.savingsAmount || metadata?.estimated_savings || 0;
                  const negotiatedCost = originalCost - savingsAmount;
                  const savingsPercentage =
                    originalCost > 0 && savingsAmount > 0
                      ? Math.round((savingsAmount / originalCost) * 100)
                      : 0;

                  // Only show single pricing if no pricing tiers are present
                  if (originalCost > 0 && savingsAmount > 0 && !metadata?.pricingTiers) {
                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          mt: 3,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'success.200',
                          bgcolor: alpha('#4caf50', 0.05),
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}
                        >
                          Cost Savings
                        </Typography>
                        <Stack direction="row" spacing={4} flexWrap="wrap">
                          <Box>
                            <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                              Original Cost
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: 'secondary.700',
                                textDecoration: 'line-through',
                              }}
                            >
                              ${originalCost.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                              Negotiated Cost
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: 'success.main' }}
                            >
                              ${negotiatedCost.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                              You Save
                            </Typography>
                            <Stack direction="row" alignItems="baseline" spacing={1}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, color: 'success.main' }}
                              >
                                ${savingsAmount.toFixed(2)}
                              </Typography>
                              <Chip
                                label={`${savingsPercentage}% OFF`}
                                size="small"
                                sx={{
                                  bgcolor: 'success.main',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </Stack>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  }
                  return null;
                })()}

                {/* Vendor Information - Show only to members or show message for non-members */}
                {(() => {
                  const metadata = groupNegotiation.metadata as
                    | {
                        vendor?: {
                          name?: string;
                          email?: string;
                          phone?: string;
                        };
                      }
                    | undefined;
                  const vendor = metadata?.vendor;

                  if (vendor && (vendor.name || vendor.email || vendor.phone)) {
                    if (isUserMember) {
                      return (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mt: 3,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}
                          >
                            Vendor Contact Information
                          </Typography>
                          <Stack spacing={2}>
                            {vendor.name && (
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <User01Icon sx={{ width: 20, height: 20, color: '#6B7280' }} />
                                <Typography variant="body1" sx={{ color: 'secondary.700' }}>
                                  {vendor.name}
                                </Typography>
                              </Stack>
                            )}
                            {vendor.email && (
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Mail01Icon sx={{ width: 20, height: 20, color: '#6B7280' }} />
                                <Typography
                                  variant="body1"
                                  component="a"
                                  href={`mailto:${vendor.email}`}
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {vendor.email}
                                </Typography>
                              </Stack>
                            )}
                            {vendor.phone && (
                              <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Phone01Icon sx={{ width: 20, height: 20, color: '#6B7280' }} />
                                <Typography
                                  variant="body1"
                                  component="a"
                                  href={`tel:${vendor.phone}`}
                                  sx={{
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline',
                                    },
                                  }}
                                >
                                  {formatPhoneNumber(vendor.phone)}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Paper>
                      );
                    }
                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          mt: 3,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: alpha('#2196f3', 0.3),
                          bgcolor: alpha('#2196f3', 0.05),
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <InfoCircleIcon sx={{ color: '#1976d2' }} />
                          <Typography variant="body1" sx={{ color: 'secondary.700' }}>
                            Vendor contact information will be visible after you join the group
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  }
                  return null;
                })()}
              </Box>

              {/* Mobile Layout - Stacked */}
              <Stack sx={{ display: { xs: 'flex', sm: 'none' }, spacing: 2 }}>
                {/* Group Overview - Mobile */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
                    Group Overview
                  </Typography>

                  <Stack spacing={2}>
                    {/* Created Date */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(groupNegotiation.created_at)}
                      </Typography>
                    </Box>

                    {/* End Date */}
                    {groupNegotiation.end_date && (
                      <Box>
                        <Typography
                          variant="body2"
                          color={isGroupExpired ? 'error.main' : 'secondary.600'}
                          sx={{ mb: 0.5 }}
                        >
                          {isGroupExpired ? 'Expired' : 'Ends'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: isGroupExpired ? 'error.main' : 'inherit',
                          }}
                        >
                          {formatDate(groupNegotiation.end_date)}
                        </Typography>
                      </Box>
                    )}

                    {/* Initiator */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Initiated by
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {groupNegotiation.initiator.full_name || groupNegotiation.initiator.email}
                      </Typography>
                    </Box>

                    {/* Member Count */}
                    <Box>
                      <Typography variant="body2" color="secondary.600" sx={{ mb: 0.5 }}>
                        Members
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {groupNegotiation.members.length}{' '}
                        {groupNegotiation.members.length === 1 ? 'member' : 'members'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* AI Research Section - Mobile */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
                    AI Research
                  </Typography>
                  {isUserMember ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', mb: 2, display: 'block' }}
                      >
                        Disclaimer: The AI-generated research provided here is for informational
                        purposes only and should not be considered legal, financial, or professional
                        advice. Please consult with a qualified expert before making any decisions
                        based on this information.
                      </Typography>
                      {/* Show loading state if loading */}
                      {isResearchLoading && !aiResearchResponse && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          minHeight={200}
                        >
                          <CircularProgress />
                        </Box>
                      )}

                      {/* Show AI response if available */}
                      {aiResearchResponse && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha('#9D1500', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#9D1500', 0.1),
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            color="primary"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              mb: 1,
                            }}
                          >
                            AI Research Analysis:
                          </Typography>
                          <ReactMarkdown components={markdownComponents}>
                            {aiResearchResponse}
                          </ReactMarkdown>
                        </Box>
                      )}

                      {/* Show error message if failed */}
                      {aiResearchError && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha('#f44336', 0.02),
                            border: '1px solid',
                            borderColor: alpha('#f44336', 0.1),
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            AI research is not available at the moment
                          </Typography>
                        </Box>
                      )}

                      {/* Show placeholder if no response and not loading and no error */}
                      {!isResearchLoading && !aiResearchResponse && !aiResearchError && (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'secondary.50',
                            border: '2px dashed',
                            borderColor: 'secondary.200',
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="secondary.600"
                            sx={{
                              fontStyle: 'italic',
                              fontSize: '0.875rem',
                            }}
                          >
                            AI research is being generated...
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: alpha('#2196f3', 0.05),
                        border: '1px solid',
                        borderColor: alpha('#2196f3', 0.3),
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Join the group to access AI-powered research insights
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Stack>
          )}

          {/* Members Tab - Only show if user is a member */}
          {isUserMember && activeTab === 1 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: { xs: 1, sm: 2 },
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Search Bar - Mobile Only */}
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <Toolbar
                  sx={{
                    height: { xs: 56, sm: 64 },
                    display: 'flex',
                    justifyContent: 'center',
                    p: { xs: 2, sm: 3 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'secondary.lighter',
                  }}
                >
                  <OutlinedInput
                    fullWidth
                    value={filterName}
                    onChange={handleFilterName}
                    placeholder="Search members..."
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchMdIcon sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    }
                    sx={{
                      maxWidth: { xs: '100%', sm: '100%' },
                      bgcolor: 'background.paper',
                    }}
                  />
                </Toolbar>
              </Box>

              {/* Content Area */}
              <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'background.paper' }}>
                {(() => {
                  if (isMobile) {
                    if (filteredMembers.length === 0) {
                      return renderEmptyState();
                    }
                    return renderMobileMembersList();
                  }

                  // Desktop Layout - Match CommunityViewPage format
                  return (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: 'secondary.darker',
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                          }}
                        >
                          Members
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'secondary.darker',
                              mx: 1,
                              verticalAlign: 'middle',
                            }}
                          />
                          {filteredMembers.length}
                        </Typography>
                      </Box>
                      <Stack spacing={{ xs: 1.5, sm: 2 }}>
                        {filteredMembers.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                              No members yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Members will appear here when they join the group.
                            </Typography>
                          </Box>
                        ) : (
                          filteredMembers.map(member => (
                            <Paper
                              key={member.id}
                              elevation={0}
                              sx={{
                                p: { xs: 2, sm: 2.5 },
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'secondary.100',
                                bgcolor: 'secondary.50',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  borderColor: alpha('#9D1500', 0.2),
                                  bgcolor: alpha('#9D1500', 0.02),
                                },
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={3}>
                                <Avatar
                                  {...getAvatarProps(member.user.full_name)}
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    fontSize: '1.25rem',
                                    fontWeight: 600,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                  }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ mb: 1 }}
                                  >
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: 600, color: 'secondary.900' }}
                                    >
                                      {member.user.full_name}
                                    </Typography>
                                    {member.user.id === groupNegotiation.initiator.user_id && (
                                      <Chip
                                        label="Creator"
                                        size="small"
                                        sx={{
                                          bgcolor: 'primary.main',
                                          color: 'white',
                                          fontWeight: 500,
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography variant="body2" color="secondary.600">
                                      Joined {formatDate(member.joined_at)}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </Paper>
                          ))
                        )}
                      </Stack>
                    </>
                  );
                })()}
              </Box>
            </Paper>
          )}

          {/* Posts Tab - Adjust tab index based on member status */}
          {((isUserMember && activeTab === 2) || (!isUserMember && activeTab === 1)) &&
            (isMobile ? (
              <Paper
                elevation={0}
                sx={{
                  mb: 1,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    p: 1.875,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'background.paper',
                    color: 'secondary.900',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Group Discussions
                  </Typography>
                  {isUserMember && (
                    <Button
                      startIcon={<PlusIcon />}
                      variant="contained"
                      onClick={handleCreatePostClick}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 500,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                          boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                        },
                      }}
                    >
                      Create Post
                    </Button>
                  )}
                </Box>

                <Box sx={{ p: 2 }}>
                  {(() => {
                    if (!posts || posts.length === 0) {
                      return (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 4,
                            px: 3,
                            borderRadius: 2,
                            border: '2px dashed',
                            borderColor: 'secondary.200',
                            bgcolor: alpha('#f5f5f5', 0.5),
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="secondary.600"
                            sx={{ fontStyle: 'italic' }}
                          >
                            No discussions yet. Be the first to start a conversation!
                          </Typography>
                        </Box>
                      );
                    }

                    return posts.map((post: PostResponse, index: number) => (
                      <PostDiscussion
                        key={post.id}
                        post={post}
                        index={index}
                        totalPosts={posts.length}
                        communityId={parseInt(negotiationId, 10)}
                        replyInputs={replyInputs}
                        showReplyForms={showReplyForms}
                        expandedReplies={expandedReplies}
                        onTogglePostLike={handleTogglePostLike}
                        onShowReplyForm={handleShowReplyForm}
                        onReplyInputChange={handleReplyInputChange}
                        onSubmitReply={handleSubmitReply}
                        onToggleExpandedReplies={handleToggleExpandedReplies}
                        onToggleReplyLike={handleToggleReplyLike}
                        getAvatarProps={getAvatarProps}
                        togglePostLikeMutation={togglePostLikeMutation}
                        createReplyMutation={createReplyMutation}
                      />
                    ));
                  })()}
                </Box>
              </Paper>
            ) : (
              /* Desktop: centered layout */
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    height: 'fit-content',
                    width: '100%',
                  }}
                >
                  <GroupPostsTab
                    groupId={parseInt(negotiationId, 10)}
                    isUserMember={!!isUserMember}
                    isUserAdmin={false} // Groups don't have admin concept like communities
                  />
                </Paper>
              </Box>
            ))}

          {/* Negotiation AI Tab - Only for active members */}
          {isUserMember && groupNegotiation.status === 'ACTIVE' && activeTab === 3 && (
            <NegotiationAITab
              communityId={parseInt(communityId, 10)}
              negotiationId={parseInt(negotiationId, 10)}
            />
          )}
        </Stack>
      </Container>

      {/* Close Group Confirmation Dialog */}
      <Dialog
        open={closeConfirmDialogOpen}
        onClose={() => setCloseConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to close "{groupNegotiation?.title}"? This will prevent new
            members from joining and mark the group as closed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmCloseGroup}
            variant="contained"
            color="primary"
            disabled={isProcessing}
          >
            {isProcessing ? 'Closing...' : 'Close Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Group Modal */}
      {groupNegotiation && (
        <ShareGroupModal
          open={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          groupTitle={groupNegotiation.title}
          groupId={parseInt(negotiationId, 10)}
          communityId={parseInt(communityId, 10)}
          groupDescription={groupNegotiation.description}
          memberCount={groupNegotiation.members.length}
          metadata={
            groupNegotiation.metadata as {
              originalCost?: number;
              savingsAmount?: number;
              pricingTiers?: Array<{
                minHomes: number;
                maxHomes?: number;
                pricePerHome: number;
              }>;
              estimated_savings?: number;
            }
          }
        />
      )}

      {/* Location Alert Dialog */}
      <Dialog
        open={showLocationAlert}
        onClose={() => setShowLocationAlert(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            color: 'secondary.900',
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MarkerPin01Icon width={24} height={24} />
          Location Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To join Save Together groups, you need to add your location first. This helps us connect
            you with neighbors in your area for group savings.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => setShowLocationAlert(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const currentPath = `/property-manager/communities/${communityId}/groups/${negotiationId}`;
                history.push(
                  `/property-manager/property-locations/create?returnUrl=${encodeURIComponent(
                    currentPath
                  )}`
                );
              }}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              Add Location
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Group Options Menu */}
      <Menu
        anchorEl={groupMenuAnchor}
        open={Boolean(groupMenuAnchor)}
        onClose={handleGroupMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
              minWidth: 120,
              py: 0.5,
            },
          },
        }}
      >
        {/* Share Button - Show only for active groups when user is member or initiator */}
        {groupNegotiation?.status === 'ACTIVE' && (isUserMember || isUserInitiator) && (
          <MenuItem
            onClick={() => {
              setShareModalOpen(true);
              handleGroupMenuClose();
            }}
            sx={{
              py: 1,
              px: 2,
              '&:hover': {
                bgcolor: alpha('#9D1500', 0.08),
              },
            }}
          >
            <Share01Icon width={14} height={14} />
            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
              Share
            </Typography>
          </MenuItem>
        )}

        {/* Leave Group - Show for members who are not the initiator */}
        {groupNegotiation?.status === 'ACTIVE' && isUserMember && !isUserInitiator && (
          <MenuItem
            onClick={() => {
              handleLeave();
              handleGroupMenuClose();
            }}
            disabled={isProcessing}
            sx={{
              py: 1,
              px: 2,
              color: 'error.main',
              '&:hover': {
                bgcolor: alpha('#f44336', 0.08),
              },
            }}
          >
            <Users02Icon width={14} height={14} />
            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
              {isProcessing ? 'Leaving...' : 'Leave Group'}
            </Typography>
          </MenuItem>
        )}

        {/* Close Group - Show only for initiator */}
        {groupNegotiation?.status === 'ACTIVE' && isUserInitiator && (
          <MenuItem
            onClick={() => {
              handleClose();
              handleGroupMenuClose();
            }}
            disabled={isProcessing}
            sx={{
              py: 1,
              px: 2,
              color: 'error.main',
              '&:hover': {
                bgcolor: alpha('#f44336', 0.08),
              },
            }}
          >
            <Users02Icon width={14} height={14} />
            <Typography variant="body2" sx={{ ml: 1, fontSize: '0.875rem' }}>
              {isProcessing ? 'Closing...' : 'Close Group'}
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Create Post Dialog */}
      <Dialog
        open={showCreatePostForm}
        onClose={handleCancelCreatePost}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            mx: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            color: 'secondary.900',
            pb: 1,
          }}
        >
          Create New Post
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <PostForm onSubmit={handleCreatePost} onCancel={handleCancelCreatePost} mode="create" />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GroupNegotiationDetailPage;
